/**
 * DragDebugOverlay - Real-time debug overlay for building drag interactions
 *
 * Shows:
 * - Current drag/rotate state from Zustand store
 * - Map dragPan enabled/disabled status
 * - Effect run counter
 * - Event log with timestamps
 *
 * Add this to BuildingEditorMap to debug drag issues.
 */

import { useEffect, useState, useRef, useCallback } from 'react'
import { useBuildingEditorStore } from '@/stores/buildingEditorStore'

interface LogEntry {
  time: string
  msg: string
  type: 'info' | 'start' | 'end' | 'warn' | 'error' | 'effect'
}

interface DragDebugOverlayProps {
  map: mapboxgl.Map | null
  effectRunCount?: number
}

// Global effect counter (persists across component remounts)
let globalEffectCounter = 0

export function DragDebugOverlay({ map, effectRunCount }: DragDebugOverlayProps) {
  const [debugState, setDebugState] = useState({
    isDragging: false,
    isRotating: false,
    dragPanEnabled: true,
    mode: 'view' as string,
    previewBuilding: false,
  })

  const [logs, setLogs] = useState<LogEntry[]>([])
  const prevDraggingRef = useRef(false)
  const prevRotatingRef = useRef(false)

  const log = useCallback((msg: string, type: LogEntry['type'] = 'info') => {
    const time = new Date().toISOString().substr(11, 12)
    console.log(`[DEBUG-OVERLAY] ${msg}`)
    setLogs((prev) => [{ time, msg, type }, ...prev.slice(0, 30)])
  }, [])

  // Subscribe to store changes
  useEffect(() => {
    const unsubscribe = useBuildingEditorStore.subscribe((state) => {
      const newDragging = state.isDragging
      const newRotating = state.isRotating

      // Log state changes
      if (newDragging !== prevDraggingRef.current) {
        log(`isDragging: ${prevDraggingRef.current} -> ${newDragging}`, newDragging ? 'start' : 'end')
        prevDraggingRef.current = newDragging
      }
      if (newRotating !== prevRotatingRef.current) {
        log(`isRotating: ${prevRotatingRef.current} -> ${newRotating}`, newRotating ? 'start' : 'end')
        prevRotatingRef.current = newRotating
      }

      setDebugState((prev) => ({
        ...prev,
        isDragging: newDragging,
        isRotating: newRotating,
        mode: state.mode,
        previewBuilding: !!state.previewBuilding,
      }))
    })

    log('Store subscription active')

    return () => {
      unsubscribe()
      log('Store subscription removed', 'warn')
    }
  }, [log])

  // Track dragPan status by polling (no direct event available)
  useEffect(() => {
    if (!map) return

    let lastStatus: boolean | null = null

    const checkDragPan = () => {
      try {
        // Access internal state
        const isEnabled = map.dragPan.isEnabled()
        if (lastStatus !== isEnabled) {
          log(`dragPan: ${lastStatus} -> ${isEnabled}`, isEnabled ? 'end' : 'warn')
          lastStatus = isEnabled
        }
        setDebugState((prev) => ({ ...prev, dragPanEnabled: isEnabled }))
      } catch {
        // dragPan might not be available yet
      }
    }

    const interval = setInterval(checkDragPan, 100)
    checkDragPan() // Initial check

    return () => clearInterval(interval)
  }, [map, log])

  // Log effect run count changes
  useEffect(() => {
    if (effectRunCount !== undefined && effectRunCount > 0) {
      globalEffectCounter = effectRunCount
      log(`Effect run #${effectRunCount}`, 'effect')
    }
  }, [effectRunCount, log])

  return (
    <div
      className="fixed top-20 left-3 bg-black/95 text-green-400 p-4 font-mono text-xs z-[10000] rounded-lg min-w-[280px] shadow-xl"
      style={{ border: '1px solid #333' }}
    >
      <div className="text-white font-bold text-sm mb-3 pb-2 border-b border-gray-700 flex items-center gap-2">
        <span className="text-lg">🔧</span>
        DRAG DEBUG
      </div>

      <div className="space-y-1.5">
        <div className="flex justify-between">
          <span className="text-gray-500">Mode:</span>
          <span
            className={
              debugState.mode === 'add'
                ? 'text-green-400'
                : debugState.mode === 'edit'
                  ? 'text-yellow-400'
                  : 'text-gray-400'
            }
          >
            {debugState.mode}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-500">Preview Building:</span>
          <span className={debugState.previewBuilding ? 'text-green-400' : 'text-gray-600'}>
            {String(debugState.previewBuilding)}
          </span>
        </div>

        <div className="h-px bg-gray-800 my-2" />

        <div className="flex justify-between">
          <span className="text-gray-500">isDragging:</span>
          <span className={debugState.isDragging ? 'text-green-400 font-bold animate-pulse' : 'text-gray-600'}>
            {String(debugState.isDragging)}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-500">isRotating:</span>
          <span className={debugState.isRotating ? 'text-blue-400 font-bold animate-pulse' : 'text-gray-600'}>
            {String(debugState.isRotating)}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-500">dragPan:</span>
          <span
            className={
              debugState.dragPanEnabled ? 'text-gray-500' : 'text-yellow-400 font-bold'
            }
          >
            {debugState.dragPanEnabled ? 'enabled' : 'DISABLED'}
          </span>
        </div>

        <div className="h-px bg-gray-800 my-2" />

        <div className="flex justify-between">
          <span className="text-gray-500">Effect Runs:</span>
          <span className={globalEffectCounter > 1 ? 'text-red-400 font-bold' : 'text-white'}>
            {globalEffectCounter || effectRunCount || '-'}
          </span>
        </div>
      </div>

      {/* Event Log */}
      <div className="mt-3 pt-3 border-t border-gray-700">
        <div className="text-gray-500 text-[10px] mb-1">Event Log:</div>
        <div className="text-[10px] max-h-[150px] overflow-y-auto">
          {logs.map((l, i) => (
            <div
              key={i}
              className={`py-0.5 ${
                l.type === 'start'
                  ? 'text-green-400'
                  : l.type === 'end'
                    ? 'text-cyan-400'
                    : l.type === 'warn'
                      ? 'text-yellow-400'
                      : l.type === 'error'
                        ? 'text-red-400'
                        : l.type === 'effect'
                          ? 'text-purple-400'
                          : 'text-gray-400'
              }`}
              style={{ opacity: i === 0 ? 1 : Math.max(0.4, 1 - i * 0.05) }}
            >
              <span className="text-gray-600">[{l.time}]</span> {l.msg}
            </div>
          ))}
          {logs.length === 0 && <div className="text-gray-600 italic">No events yet</div>}
        </div>
      </div>
    </div>
  )
}

// Export the global counter for use in hooks
export function incrementEffectCounter(): number {
  globalEffectCounter++
  return globalEffectCounter
}

export function getEffectCounter(): number {
  return globalEffectCounter
}
